import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, TrendingUp, Home, Building2, FileText, PhoneCall, MessageSquare, Trophy, Clock, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { Card, EmptyState, Badge } from "../AdminUI";

const Stat = ({ icon: Icon, label, value, color = "blue", to }) => {
  const bg = { blue: "bg-[var(--action)]/10 text-[var(--action)]", red: "bg-[var(--red)]/10 text-[var(--red)]", green: "bg-green-100 text-green-600", navy: "bg-[var(--navy)]/10 text-[var(--navy)]" }[color];
  const inner = (
    <div className="bg-white border border-gray-200 rounded-xl p-5 h-full hover:border-[var(--action)]/40 transition-colors">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${bg}`}><Icon className="w-5 h-5" /></div>
      <p className="text-3xl font-head font-extrabold text-[var(--navy)] mt-3">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [recent, setRecent] = useState([]);
  const [reminders, setReminders] = useState([]);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setRecent(r.data.slice(0, 6))).catch(() => {});
    api.get("/admin/tasks").then((r) => setReminders(r.data.filter((t) => ["Open", "In Progress", "Overdue"].includes(t.status)).slice(0, 6))).catch(() => {});
  }, []);
  if (!d) return null;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Dashboard</h1>
        <p className="text-gray-500 text-sm">Overview of leads and activity.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="New Leads" value={d.new_leads} color="red" to="/admin/leads" />
        <Stat icon={TrendingUp} label="Leads This Week" value={d.leads_this_week} color="blue" />
        <Stat icon={Home} label="Residential Leads" value={d.residential_leads} color="navy" />
        <Stat icon={Building2} label="Commercial Leads" value={d.commercial_leads} color="navy" />
        <Stat icon={FileText} label="Quote Requests" value={d.quote_requests} color="blue" />
        <Stat icon={PhoneCall} label="Callback Requests" value={d.callback_requests} color="red" />
        <Stat icon={MessageSquare} label="Ghost AI Chats" value={d.ghost_conversations} color="blue" to="/admin/conversations" />
        <Stat icon={Trophy} label="Won Leads" value={d.won_leads} color="green" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Conversion" className="lg:col-span-1">
          <div className="space-y-4">
            <div><p className="text-4xl font-head font-extrabold text-[var(--navy)]">{d.conversion_rate}%</p><p className="text-sm text-gray-500">Conversion rate</p></div>
            <div className="flex gap-6">
              <div><p className="text-2xl font-bold text-green-600">{d.won_leads}</p><p className="text-xs text-gray-500">Won</p></div>
              <div><p className="text-2xl font-bold text-red-500">{d.lost_leads}</p><p className="text-xs text-gray-500">Lost</p></div>
              <div><p className="text-2xl font-bold text-amber-500">{d.pending_followups}</p><p className="text-xs text-gray-500">Follow-ups</p></div>
            </div>
          </div>
        </Card>
        <Card title="Leads by Source" className="lg:col-span-2">
          {Object.keys(d.leads_by_source).length === 0 ? <EmptyState icon={Clock} title="No leads yet" hint="Leads will appear here as they come in." /> : (
            <div className="space-y-2">
              {Object.entries(d.leads_by_source).map(([k, v]) => (
                <div key={k} className="flex items-center gap-3">
                  <span className="w-40 text-sm text-gray-600 capitalize">{k.replace(/_/g, " ")}</span>
                  <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[var(--action)]" style={{ width: `${(v / d.total_leads) * 100}%` }} /></div>
                  <span className="text-sm font-semibold text-[var(--navy)]">{v}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {reminders.length > 0 && (
        <Card title={<span className="flex items-center gap-2"><ListChecks className="w-4 h-4" /> Follow-Up Reminders</span>} action={<Link to="/admin/follow-ups" className="text-sm text-[var(--action)] hover:underline">View all</Link>}>
          <div className="divide-y divide-gray-100" data-testid="dashboard-reminders">
            {reminders.map((t) => (
              <Link to="/admin/follow-ups" key={t.id} className="flex items-center justify-between py-2.5 hover:bg-gray-50 px-2 -mx-2 rounded">
                <div><p className="font-semibold text-sm text-[var(--navy)]">{t.title}</p><p className="text-xs text-gray-400">{t.type}{t.due_date && ` · Due ${t.due_date}`}</p></div>
                <Badge color={t.status === "Overdue" || (t.due_date && t.due_date < today) ? "red" : "amber"}>{t.status === "Overdue" || (t.due_date && t.due_date < today) ? "Overdue" : t.due_date === today ? "Today" : t.priority}</Badge>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <Card title="Recent Leads" action={<Link to="/admin/leads" className="text-sm text-[var(--action)] hover:underline">View all</Link>}>
        {recent.length === 0 ? <EmptyState icon={Users} title="No leads yet" hint="New quote requests and Ghost AI leads will show here." /> : (
          <div className="divide-y divide-gray-100">
            {recent.map((l) => (
              <Link to="/admin/leads" key={l.id} className="flex items-center justify-between py-3 hover:bg-gray-50 px-2 -mx-2 rounded">
                <div><p className="font-semibold text-[var(--navy)]">{l.name || "Unknown"} <span className="text-xs text-gray-400 font-normal">{l.lead_id}</span></p>
                  <p className="text-xs text-gray-500">{l.services?.join(", ") || "—"} · {l.source}</p></div>
                <span className="text-xs bg-gray-100 px-2.5 py-1 rounded-full">{l.status}</span>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
